import React from "react";
import {
  SelectedProductEntityContext,
  SelectedProductEntityType,
} from "./context";
import {
  ConvertMainProductFromTemplateInput,
  Product,
  Success,
} from "@towersystems/roam-common/lib/generated-types";
import { useMutation } from "@apollo/client";
import {
  DANGEROUSLY_DELETE_ALL_PRODUCT_VARIANTS_AND_ASSOCIATED_DATA,
  MUTATION_CONVERT_MAIN_PRODUCT_FROM_TEMPLATE,
  MUTATION_CREATE_VARIANT_FOR_PRODUCT,
  MUTATION_DUPLICATE_PRODUCT,
  MUTATION_GENERATE_BARCODE,
  MUTATION_REMOVE_ALL_VARIANT_DATA_FROM_PRODUCT,
  MUTATION_REMOVE_PRODUCT_FROM_PARENT,
  MUTATION_SEND_PRODUCT_INBOUND_RETAILER,
} from "./constants";
import { useFlashMessages } from "@towersystems/roam-common-native";
import { CreateVariantChildFormValues } from "../../components/CreateVariantChildForm/types";
import { DuplicateProductFormValues } from "../../components/DuplicateProductForm/types";
import { Json } from "@towersystems/roam-common/lib/shared-types";

export const useSelectedProductEntity = (
  product: Product
): SelectedProductEntityType => {
  const [_product, _setSelectedProduct] = React.useState<Product>(product);

  const setProduct = React.useCallback(
    (p: Product) => {
      _setSelectedProduct(p);
    },
    [_setSelectedProduct]
  );

  React.useEffect(() => {
    _setSelectedProduct(product);
  }, [product]);

  return {
    product: _product,
    setProduct,
  };
};

export const useExportProductInboundRetailer = () => {
  const [m, { loading }] = useMutation<{ sendProductInboundRetailer: Success }>(
    MUTATION_SEND_PRODUCT_INBOUND_RETAILER
  );

  const flashMessagesContext = useFlashMessages();

  const handleSendProductInboundRetailer = React.useCallback(
    async (
      productId: string,
      {
        successCallback,
      }: {
        successCallback?: () => void;
      } = {}
    ) => {
      try {
        const result = await m({
          variables: {
            productId,
          },
        });

        if (result.data?.sendProductInboundRetailer.success) {
          flashMessagesContext.showMessage({
            message: "Product successfully inbound to retailer!",
            severity: "success",
          });
          successCallback?.();
        } else {
          throw new Error("Failed to send inbound to retailer");
        }
      } catch (err) {
        flashMessagesContext.showMessage({
          message: "Opps! Something went wrong",
          severity: "error",
        });
      }
    },
    [m]
  );

  return {
    loading,
    sendProductInboundRetailer: handleSendProductInboundRetailer,
  };
};

export const useRemoveProductFromParent = () => {
  const [m, { loading }] = useMutation<{ removeProductFromParent: Product }>(
    MUTATION_REMOVE_PRODUCT_FROM_PARENT
  );
  const flashMessagesContext = useFlashMessages();
  const handleRemoveProductFromParent = React.useCallback(
    async (
      productId: string,
      {
        successCallback,
      }: {
        successCallback?: (product: Product) => Promise<void> | void;
      } = {}
    ) => {
      try {
        const result = await m({
          variables: {
            productId,
          },
        });

        if (result.data?.removeProductFromParent) {
          await successCallback?.(result.data.removeProductFromParent);
        } else {
          throw new Error();
        }
      } catch (err: any) {
        flashMessagesContext.showMessage({
          message: err.message ?? "Opps! Something went wrong",
          severity: "error",
        });
      }
    },
    [m]
  );

  return {
    handleRemoveProductFromParent,
    loading,
  };
};

export const useRemoveAllVariantDataFromProduct = () => {
  const [m, { loading }] = useMutation<{
    removeAllVariantDataFromProduct: Product;
  }>(MUTATION_REMOVE_ALL_VARIANT_DATA_FROM_PRODUCT);

  const flashMessagesContext = useFlashMessages();

  const handleRemoveAllVariantDataFromProduct = React.useCallback(
    async (
      productId: string,
      {
        successCallback,
      }: {
        successCallback?: (product: Product) => Promise<void> | void;
      } = {}
    ) => {
      try {
        const result = await m({
          variables: {
            productId,
          },
        });
        console.log("result", result);
        if (result.data?.removeAllVariantDataFromProduct) {
          flashMessagesContext.showMessage({
            message: "Successfully removed all variant data from product",
            severity: "success",
          });
          await successCallback?.(result.data.removeAllVariantDataFromProduct);
        }
      } catch (e: any) {
        flashMessagesContext.showMessage({
          message: e?.message ?? "Something went wrong",
          severity: "error",
        });
      }
    },
    [m]
  );

  return { handleRemoveAllVariantDataFromProduct, loading };
};

export const useSelectedProductEntityContextProvider = () => {
  const context = React.useContext(SelectedProductEntityContext);
  if (!context) {
    throw new Error("No SelectedProductContext context was provided.");
  }
  return context;
};

export const useConvertMainProductFromTemplate = () => {
  const [m, { loading }] = useMutation<{
    convertMainProductFromTemplate: Product;
  }>(MUTATION_CONVERT_MAIN_PRODUCT_FROM_TEMPLATE);

  // The caller MUST handle the error
  const handleConvertMainProductFromTemplate = React.useCallback(
    async (
      input: ConvertMainProductFromTemplateInput,
      {
        successCallback,
      }: {
        successCallback?: (product: Product) => Promise<void> | void;
      } = {}
    ) => {
      const result = await m({
        variables: {
          input,
        },
      });
      if (result.data?.convertMainProductFromTemplate) {
        await successCallback?.(result.data.convertMainProductFromTemplate);
      } else {
        throw new Error("Something went wrong");
      }
    },
    [m]
  );

  return {
    handleConvertMainProductFromTemplate,
    loading,
  };
};

export const useCreateVariantForProduct = () => {
  const [m, { loading }] = useMutation<{
    createVariantForProduct: Product;
  }>(MUTATION_CREATE_VARIANT_FOR_PRODUCT);

  // The call MUST handle the error
  const handleCreateVariantForProduct = React.useCallback(
    async (
      input: CreateVariantChildFormValues,
      {
        successCallback,
      }: {
        successCallback?: (product: Product) => Promise<void> | void;
      } = {}
    ) => {
      const result = await m({
        variables: {
          input,
        },
      });
      if (result.data?.createVariantForProduct) {
        await successCallback?.(result.data.createVariantForProduct);
      } else {
        throw new Error();
      }
    },
    [m]
  );

  return {
    handleCreateVariantForProduct,
    loading,
  };
};

export const useDuplicateProduct = () => {
  const [m, { loading }] = useMutation<{
    duplicateProduct: Product;
  }>(MUTATION_DUPLICATE_PRODUCT);

  // The callr MUST handle the error
  const handleDuplicateProduct = React.useCallback(
    async (
      input: DuplicateProductFormValues,
      {
        successCallback,
      }: {
        successCallback?: (product: Product) => Promise<void> | void;
      } = {}
    ) => {
      const result = await m({
        variables: {
          input,
        },
      });
      if (result.data?.duplicateProduct) {
        await successCallback?.(result.data.duplicateProduct);
      } else {
        throw new Error();
      }
    },
    [m]
  );

  return {
    handleDuplicateProduct,
    loading,
  };
};

export const useGenerateProductBarcode = () => {
  const flashMessagesContext = useFlashMessages();

  const [generateBarcodeMutation] = useMutation<{
    generateProductBarcode: string;
  }>(MUTATION_GENERATE_BARCODE);

  const handleGenerateBarcode = React.useCallback(async () => {
    try {
      const result = await generateBarcodeMutation();
      if (!result.data?.generateProductBarcode) {
        throw new Error("Failed to generate barcode");
      }
      const barcode = result.data.generateProductBarcode;
      return barcode;
    } catch (e: any) {
      const msg = e?.message ?? "Something went wrong";
      flashMessagesContext.showMessage({
        message: msg,
        severity: "error",
      });
    }
  }, []);

  return {
    handleGenerateBarcode,
  };
};

export const useRemoveAllProductVariantAndAssociatedData = () => {
  const [m, { loading }] = useMutation<{
    dangerouslyDeleteAllPartnerProductVariantsData: Success;
  }>(DANGEROUSLY_DELETE_ALL_PRODUCT_VARIANTS_AND_ASSOCIATED_DATA);

  const flashMessagesContext = useFlashMessages();

  const handleDangerouslyDeleteAllProductVariantsAndAssociatedData =
    React.useCallback(async () => {
      try {
        const result = await m();
        if (result.data?.dangerouslyDeleteAllPartnerProductVariantsData) {
          flashMessagesContext.showMessage({
            message:
              "Successfully deleted all product variants and associated data. App will be restarted shortly.",
            severity: "success",
          });

          // delay
          setTimeout(() => {
            // reload browser app
            window.location.reload();
          }, 2000);
        }
      } catch (e: any) {
        flashMessagesContext.showMessage({
          message: e?.message ?? "Something went wrong",
          severity: "error",
        });
      }
    }, [m]);

  return {
    handleDangerouslyDeleteAllProductVariantsAndAssociatedData,
    loading,
  };
};
